// SelectionSummary — Récapitulatif sticky des soins cochés (Matin / Midi / Soir)
// Chips colorées par période, pop-in animé, retrait au clic

import { useMemo } from 'react';
import { CARE_CATEGORIES, PERIODS } from '../data/careData';
import { PERIOD_COLORS } from './CareDrawer';

interface SelectionSummaryProps {
  selectedByPeriod: Record<string, string[]>;
  onToggleItem: (period: 'matin' | 'midi' | 'soir', id: string) => void;
}

export default function SelectionSummary({ selectedByPeriod, onToggleItem }: SelectionSummaryProps) {
  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    CARE_CATEGORIES.forEach((c) => c.items.forEach((i) => {
      if (!i.isHeader) map.set(i.id, i.label);
    }));
    return map;
  }, []);

  const totalCount = PERIODS.reduce((acc, p) => acc + (selectedByPeriod[p.id]?.length || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-background-200/60 overflow-hidden">
      {/* En-tête */}
      <div className="px-4 py-3 border-b border-background-100 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-file-list-3-line text-primary-600 text-sm"></i>
        </div>
        <span className="text-sm font-bold text-foreground-800 flex-1">Récapitulatif</span>
        <span
          key={totalCount}
          className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${totalCount > 0 ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-400'} animate-check-pop`}
        >
          {totalCount} soin{totalCount > 1 ? 's' : ''}
        </span>
      </div>

      {/* Contenu */}
      {totalCount === 0 ? (
        <div className="px-4 py-4 text-center">
          <p className="text-xs text-foreground-400">
            <i className="ri-mouse-line mr-1"></i>
            Cochez des soins dans les tiroirs ci-dessous
          </p>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-2.5">
          {PERIODS.map((p) => {
            const items = selectedByPeriod[p.id] || [];
            if (items.length === 0) return null;
            const colors = PERIOD_COLORS[p.color];
            return (
              <div key={p.id}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                  <span className={colors.text}>{p.label}</span>
                  <span className="text-foreground-300 font-normal">{items.length}</span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((id) => (
                    <button
                      key={id}
                      onClick={() => onToggleItem(p.id, id)}
                      title="Cliquer pour retirer"
                      className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap animate-chip-pop bg-background-50 border-background-200/70 text-foreground-700 hover:bg-background-100"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></span>
                      {labelMap.get(id) || id}
                      <i className="ri-close-line text-foreground-300 group-hover:text-foreground-500 text-xs"></i>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}